import { nextTick, onBeforeMount, provide, reactive, ref } from 'vue';

import Draft from '@/db/models/draft';
import { useStore } from '@/store';
import { deepCopy, deepSet } from '@/utils';
import axios from '@/axios';
import echo from '@/plugins/echo';
import { useToast } from '@/plugins/toast';

export const useForm = ({ props, emit }) => {
    const store = useStore();
    const toast = useToast();
    const fields = reactive({});
    const submitting = ref(false);
    let draftPromise;

    const setSubmitting = (value) => {
        submitting.value = value;
        emit('update:submitting', value);
    };

    const fieldListener = (type, event) => {
        const listeners = {
            'update:state': function (event) {
                form.saveDraft();
            }
        };

        if (listeners[type]) {
            listeners[type](event);
        }
    };

    const submitData = async ({ data, setErrors, done }) => {
        const headers = props.headers ?? {};
        headers['X-Socket-ID'] = echo.socketId();

        let response;
        try {
            response = await axios({
                method: props.method,
                url: props.url,
                transformRequest: props.transformRequestUsing,
                params: props.params,
                data,
                headers,
            });
        } catch (error) {
            if (!error.response) {
                toast.error('Request failed. Please check your internet connection or try again later.');
                console.error(error);
            } else if (error.response.status === 429) {
                toast.error('Too many requests. Please try again later.');
            } else if (error.response.status === 422) {
                toast.error('Some fields are invalid');
                setErrors(error.response.data.errors);
            } else if (error.response.status === 400) {
                toast.error(error.response.data.message || 'Invalid request.');
            } else if (error.response.status === 401) {
                toast.error('Login required.');
            } else if (error.response.status === 403) {
                toast.error('You are not authorized to perform this action.');
            } else {
                toast.error('An error occured. Please try again later.');
            }

            emit('submit:error', error);

            return;
        };

        emit('submit:success', response);

        done();
    };

    const form = {
        get markAsRequired() {
            return props.markAsRequired;
        },
        get disabled() {
            return submitting.value || props.disabled;
        },
        inDebugMode() {
            return props.debug && import.meta.env.DEV;
        },
        getFields() {
            return Object.keys(fields).map((id) => fields[id]);
        },
        getField(name) {
            return this.getFields().find((field) => field.name == name);
        },
        getState() {
            const state = {};

            this.getFields().forEach(field => {
                state[field.name] = field.state;
            });

            return state;
        },
        getInitialState() {
            const initialState = {};

            this.getFields().forEach(field => {
                initialState[field.name] = field.initialState;
            });

            return initialState;
        },
        async register(field) {
            fields[field.id] = field;

            if (typeof props.fill !== 'undefined') {
                field.hydrate(props.fill[field.name]);
            } else if (props.draftItemType) {
                const draft = await draftPromise;

                if (draft) {
                    field.hydrate(draft.content[field.name]);
                }
            }

            field.events.on('*', fieldListener);

            return;
        },
        async unregister(id) {
            const field = fields[id];

            field.events.off('*', fieldListener);

            delete fields[id];

            return;
        },
        get disabled() {
            return props.disabled;
        },
        get submitting() {
            return submitting.value;
        },
        get isDirty() {
            return this.getFields().some((field) => field.isDirty);
        },
        get isClean() {
            return !this.isDirty;
        },
        getDirtyKeys() {
            return this.getFields().filter(field => field.isDirty).map(field => field.name);
        },
        errors() {
            const errors = {};

            this.getFields().forEach(field => {
                if (field.errors?.length) {
                    errors[field.name] = field.errors;
                }
            });

            return errors;
        },
        hasErrors() {
            return Object.keys(this.errors()).length > 0;
        },
        async validate() {
            const validations = this.getFields().map(async (field) => await field.validate());

            await Promise.all(validations);

            return !this.hasErrors();
        },
        async dehydrate() {
            const beforeDehydrationCalls = this.getFields().map(async (field) => await field.beforeDehydration());
            const result = await Promise.all(beforeDehydrationCalls);

            if (!result.every((response) => response !== false)) {
                return false;
            }

            const state = {};

            const dehydrationCalls = this.getFields().map(async (field) => {
                state[field.name] = await field.dehydrate();
            });

            await Promise.all(dehydrationCalls);

            return state;
        },
        async submit() {
            const dirtyKeys = this.getDirtyKeys();
            setSubmitting(true);

            await nextTick();

            try {
                if (!await this.validate()) {
                    toast.error('Some fields are invalid.');
                    return;
                }

                const data = await this.dehydrate();

                if (data === false) {
                    return;
                }

                const dirtyData = {};

                dirtyKeys.forEach(key => dirtyData[key] = data[key]);

                const transformedData = {};
                const transformedDirtyData = {};

                Object.keys(data).forEach((key) => {
                    deepSet(transformedData, key, data[key]);
                });

                Object.keys(dirtyData).forEach((key) => {
                    deepSet(transformedDirtyData, key, dirtyData[key]);
                });

                const event = {
                    data: transformedData,
                    dirtyData: transformedDirtyData,
                    setErrors: (errors) => {
                        this.getFields().forEach(field => {
                            if (errors[field.name]) {
                                field.setErrors(errors[field.name]);
                            }
                        })
                    },
                    done: () => {
                        this.deleteDraft();

                        if (props.clearAfterSubmit) {
                            this.clear();
                        }
                    }
                };

                if (props.submitUsing) {
                    await props.submitUsing(event);
                    return;
                }

                await submitData(event);
            } finally {
                setSubmitting(false);
            }
        },
        async getDraft() {
            if (!props.draftItemType) {
                return null;
            }

            return await Draft.query().where({
                userId: store.state.user?.id,
                itemType: props.draftItemType,
                itemId: props.draftItemId,
            }).first();
        },
        async saveDraft() {
            if (!props.draftItemType) {
                return;
            }

            return await Draft.updateOrCreate({
                userId: store.state.user?.id,
                itemType: props.draftItemType,
                itemId: props.draftItemId,
            }, {
                content: deepCopy(this.getState()),
            });
        },
        async deleteDraft() {
            const draft = await this.getDraft();

            if (draft) {
                return await draft.delete();
            }

            return true;
        },
        clear(fieldName = null) {
            if (fieldName) {
                this.getField(fieldName)?.clear();
            } else {
                this.getFields().forEach(field => field.clear());
            }
        },
        reset() {
            this.getFields().forEach(field => field.reset());
        },
    }

    provide('form', form);

    onBeforeMount(() => {
        draftPromise = form.getDraft();

        if (form.inDebugMode()) {
            window.form = form;
        }
    });

    return form;
}
